import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { LoginModal, PublicNav } from '@components';

const guideData = {
    current: {
        title: "중대재해 발생 가능 현황",
        subTitle: "데이터 기반 실시간 위험도 예측 및 모니터링",
        icon: "fa-chart-line",
        color: "text-primary",
        description: "사업장 내 수집되는 다양한 안전보건 데이터를 실시간으로 통합 분석하여 중대재해 발생 가능성을 사전에 예측하고 모니터링하는 지능형 대시보드입니다. 현장의 유해·위험 요인을 선제적으로 파악하여 사고의 골든타임을 확보합니다.",
        features: [
            { title: "실시간 위험성 지수 표출", desc: "사업장/공정별 위험도를 수치화 및 색상으로 시각화하여 직관적 파악 가능" },
            { title: "과거 사고 데이터 연계", desc: "기존에 발생했던 아차사고 및 산재 데이터를 기반으로 취약 지점 집중 관리" },
            { title: "공정/설비별 위험도 맵핑", desc: "현장 도면(Map)과 연동하여 위험 구역을 특정하고 즉각적인 접근 통제 지원" },
            { title: "이상 징후 자동 알림", desc: "위험 수치 초과 시 안전관리자 및 현장 작업자에게 즉각적인 SMS/App Push 발송" }
        ],
        benefits: [
            "위험 상황 사전 인지를 통한 중대재해 발생률 획기적 감소",
            "안전보건관리책임자의 신속하고 정확한 의사결정 지원",
            "현장 중심의 선제적 안전 관리 체계 구축"
        ]
    },
    stakeholder: {
        title: "중대재해 현장 이해관계자",
        subTitle: "도급·용역·위탁 관계의 투명한 안전보건 구조 확립",
        icon: "fa-users-gear",
        color: "text-success",
        description: "원청의 근로자뿐만 아니라 하청업체, 용역 근로자를 포함한 현장의 모든 이해관계자의 안전보건 의무 이행 현황을 통합하여 관리합니다. 복잡한 도급 관계 속에서도 안전 관리의 사각지대를 제거합니다.",
        features: [
            { title: "하청업체 안전보건 역량 평가", desc: "협력업체 선정 시 안전보건 관리 수준을 시스템적으로 평가하고 이력 관리" },
            { title: "투입 인원 실시간 이력 관리", desc: "사업장 내 출입하는 모든 인력의 현황과 소속, 직무, 교육 이수 여부 파악" },
            { title: "법정 관리자 선임 현황", desc: "안전보건총괄책임자, 관리감독자 등의 지정 현황 및 역할 수행 내역 모니터링" },
            { title: "협의체 운영 기록", desc: "도급인과 수급인이 함께하는 안전보건협의체 회의록 및 안건 진행 상황 공유" }
        ],
        benefits: [
            "중대재해처벌법상 도급인의 안전보건 확보 의무 완벽 이행 및 입증",
            "협력업체와의 투명한 정보 공유로 현장 전체의 안전 수준 상향 평준화",
            "서류 작업 간소화 및 인력 관리의 효율성 증대"
        ]
    },
    signature: {
        title: "이해관계자 서명 관리",
        subTitle: "법적 효력을 갖춘 비대면 전자 서명 시스템",
        icon: "fa-file-signature",
        color: "text-warning",
        description: "TBM(Tool Box Meeting), 안전교육 참석부, 위험성 평가표, 안전작업허가서(PTW) 등 현장에서 필수로 발생하는 안전 문서에 대한 서명 작업을 모바일로 간편하게 처리하고 안전하게 보관합니다.",
        features: [
            { title: "모바일 즉시 서명", desc: "작업 전 현장에서 스마트폰이나 태블릿을 이용해 근로자 개별 전자 서명 수집" },
            { title: "이력 추적 및 위변조 방지", desc: "서명 시간, 위치(GPS), IP 정보 등을 함께 저장하여 문서의 무결성 및 신뢰성 확보" },
            { title: "미서명자 실시간 트래킹", desc: "필수 교육 및 작업 전 미서명자를 시스템이 자동 분류하여 독려 알림 발송" },
            { title: "페이퍼리스(Paperless) 실현", desc: "종이 문서 없이 모든 안전 서류를 전산화하여 검색 및 보관의 편의성 극대화" }
        ],
        benefits: [
            "종이 문서 보관 및 관리 비용 절감, 행정 업무 소요 시간 80% 단축",
            "정부 기관의 현장 점검 및 안전 감사 시 1분 이내에 완벽한 증빙 자료 제출",
            "허위 서명 방지를 통한 안전 교육의 실효성 증대"
        ]
    },
    ai: {
        title: "AI알리미",
        subTitle: "AI 기반 안전보건 질의응답 서비스",
        icon: "fa-robot",
        color: "text-warning",
        description: "중대재해처벌법, 산업안전보건법 등 안전보건 관련 법령·규정·현장 예방 조치에 대해 AI에게 자유롭게 질문하고 즉각적인 답변을 받을 수 있는 지능형 질의응답 서비스입니다. 복잡한 법령 해석부터 사고 예방 가이드라인까지 24시간 신속하게 지원합니다.",
        features: [
            { title: "법령 및 규정 해석", desc: "중대재해처벌법, 산업안전보건법 등 복잡한 법령 조항을 쉽고 명확하게 풀이하여 안내" },
            { title: "현장 위험 요인 Q&A", desc: "특정 공정이나 작업 환경에서 발생할 수 있는 위험 요인과 예방 조치를 즉시 조회" },
            { title: "질의 이력 관리", desc: "과거 AI와의 대화 이력을 저장하고 재조회하여 유사 상황에 빠르게 대처 가능" },
            { title: "FAQ 기반 빠른 답변", desc: "자주 묻는 안전보건 질문들을 카테고리별로 정리하여 별도 검색 없이 바로 확인 가능" }
        ],
        benefits: [
            "전문 법률 지식 없이도 현장 담당자 스스로 법령 준수 여부 즉각 확인 가능",
            "24시간 운영으로 긴급 상황 발생 시 야간·휴일에도 즉각적인 안전 가이드 제공",
            "반복적인 법령 문의 시간 절감으로 안전관리자의 핵심 업무 집중도 향상"
        ]
    },
    biz: {
        title: "명함관리",
        subTitle: "AI OCR 기반 명함 디지털화 · 검수 · 공유 통합 플랫폼",
        icon: "fa-id-card",
        color: "text-info",
        description: "명함 이미지를 업로드하면 AI가 영역을 자동 탐지하고 OCR로 이름·직함·연락처·이메일·주소를 추출합니다. 담당자 검수로 데이터를 확정하고, 그룹 분류·공유 링크·vCard 내보내기까지 명함 관리의 모든 과정을 하나의 플랫폼에서 처리합니다.",
        features: [
            { title: "명함 촬영 및 OCR 추출", desc: "파일 업로드 또는 카메라 촬영으로 명함을 등록하면 명함 영역을 자동 탐지·크롭하고 이름·직책·회사·전화·이메일·주소·웹사이트를 OCR로 추출" },
            { title: "AI LLM 보정 및 담당자 검수", desc: "OCR 결과를 LLM이 자동 교정하며, 담당자가 검수 화면에서 필드를 직접 수정·확정. 검수 대기→확정 완료 상태로 데이터 품질을 관리" },
            { title: "그룹 분류 및 검색", desc: "명함을 그룹으로 분류하고 그룹명 수정·삭제 가능. 이름·회사명 검색과 검수 상태 필터로 원하는 명함을 빠르게 조회" },
            { title: "공유 링크 및 vCard 내보내기", desc: "명함별 고유 URL을 생성해 로그인 없이 외부 공유 가능. .vcf 파일로 내보내 스마트폰 연락처에 바로 저장" }
        ],
        benefits: [
            "명함 이미지 한 장으로 모든 연락처 정보가 자동 입력 — 수기 입력 시간 대폭 절감",
            "검수 상태(대기·완료·오류) 추적으로 누락 없이 전체 명함 데이터 품질을 체계적으로 관리",
            "공유 링크와 vCard 내보내기로 거래처와의 연락처 교환이 종이 명함 없이도 신속하게 처리"
        ]
    },
    prevent: {
        title: "예방대책 및 이행 관리",
        subTitle: "PDCA 사이클 기반의 완벽한 재발 방지 사후 관리",
        icon: "fa-shield-heart",
        color: "text-danger",
        description: "발견된 유해·위험 요인에 대해 체계적으로 개선 대책을 수립하고, 실행부터 완료까지의 모든 과정을 추적 관리합니다. 동일한 원인의 사고가 현장에서 절대 반복되지 않도록 돕는 핵심 기능입니다.",
        features: [
            { title: "위험성 평가 연계 대책 수립", desc: "정기/수시 위험성 평가 결과와 연동하여 감소 대책 및 담당자 지정" },
            { title: "개선 조치 전/후 비교", desc: "현장 작업자가 모바일로 개선 전/후 사진을 등록하면 자동으로 결과 보고서 생성" },
            { title: "이행률 모니터링 대시보드", desc: "사업장별, 부서별 예방대책 이행 현황 및 지연 상태를 한눈에 파악" },
            { title: "미이행 과제 에스컬레이션", desc: "조치 기한 초과 시 담당자 경고 및 상위 관리자(CSO 등)에게 자동 보고 체계 가동" }
        ],
        benefits: [
            "실질적인 위험 요소 제거를 통한 안전하고 쾌적한 작업 환경 조성",
            "안전보건경영시스템(ISO 45001 등) 인증 유지 및 사후 관리 용이",
            "책임 소재를 명확히 하여 임직원의 안전 책임 의식 고취"
        ]
    }
};

const GuidePage = () => {
    const { type } = useParams();
    const navigate = useNavigate();
    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const data = guideData[type];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [type]);

    if (!data) {
        return (
            <div className="d-flex flex-column justify-content-center align-items-center vh-100 bg-light">
                <h3 className="mb-4">존재하지 않는 안내 페이지입니다.</h3>
                <Link to="/" className="btn btn-primary px-4 py-2" style={{ borderRadius: '4px' }}>홈으로 돌아가기</Link>
            </div>
        );
    }

    return (
        <div style={{ backgroundColor: '#f4f6f9', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>

            <PublicNav onLoginClick={() => setLoginModalOpen(true)} />

            {/* 상단 배너 */}
            <section className="text-white text-center" style={{ backgroundColor: '#1e2b3c', minHeight: '555px', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                <div className="container" style={{ padding: '0 1rem' }}>
                    <i className={`fa-solid ${data.icon} fa-3x ${data.color} mb-4 d-block`}></i>
                    <h1 className="fw-bold mb-3" style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)' }}>{data.title}</h1>
                    <p className="lead mb-3 fw-light text-light opacity-75">{data.subTitle}</p>
                    <p className="text-white opacity-75 mx-auto" style={{ wordBreak: 'keep-all', lineHeight: '1.7', fontSize: '1rem', maxWidth: '700px', marginBottom: type === 'biz' ? '2rem' : '0' }}>
                        {data.description}
                    </p>
                    {type === 'biz' && (
                        <button
                            className="btn btn-lg fw-bold px-5 py-3 mt-2"
                            style={{ backgroundColor: '#22D3EE', color: '#0f172a', borderRadius: '12px', fontSize: '1.1rem', border: 'none' }}
                            onClick={() => setLoginModalOpen(true)}
                        >
                            명함 목록 바로가기 <i className="fa-solid fa-arrow-right ms-2"></i>
                        </button>
                    )}
                    {type === 'ai' && (
                        <button
                            className="btn btn-lg fw-bold px-5 py-3 mt-2"
                            style={{ backgroundColor: '#5EEAD4', color: '#0f172a', borderRadius: '12px', fontSize: '1.1rem', border: 'none' }}
                            onClick={() => setLoginModalOpen(true)}
                        >
                            AI 알리미 시작하기 <i className="fa-solid fa-arrow-right ms-2"></i>
                        </button>
                    )}
                </div>
            </section>

            {/* 하단 콘텐츠 영역: flexGrow를 주어 남는 화면을 모두 채워 배너가 절대 늘어나지 않게 화면 구조를 견고히 함 */}
            <div style={{ flexGrow: 1, padding: '40px 0 60px 0' }}>
                <div className="container">
                    {/* 카드 영역 */}
                    <div className="row g-4">
                        {/* 좌측: 주요 기능 */}
                        <div className="col-lg-7">
                            <div className="card border shadow-sm h-100 text-start">
                                <div className="card-header bg-white border-bottom-0 pt-4 pb-2 px-4">
                                    <h5 className="fw-bold text-dark mb-0"><i className={`fa-solid fa-layer-group me-2 ${data.color}`}></i>상세 제공 기능</h5>
                                </div>
                                <div className="card-body px-4 pb-4">
                                    <div className="row g-3 mt-1">
                                        {data.features.map((feature, index) => (
                                            <div className="col-md-6" key={index}>
                                                <div className="p-3 bg-light rounded h-100 border border-light-subtle">
                                                    <h6 className={`fw-bold mb-2 ${data.color}`}>{feature.title}</h6>
                                                    <p className="text-muted mb-0" style={{ fontSize: '0.9rem', wordBreak: 'keep-all', lineHeight: '1.4' }}>
                                                        {feature.desc}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 우측: 기대 효과 */}
                        <div className="col-lg-5">
                            <div className="card border shadow-sm h-100 text-start">
                                <div className="card-header bg-white border-bottom-0 pt-4 pb-2 px-4">
                                    <h5 className="fw-bold text-dark mb-0"><i className="fa-solid fa-ranking-star me-2 text-warning"></i>도입 기대 효과</h5>
                                </div>
                                <div className="card-body px-4 pb-4">
                                    <ul className="list-unstyled mb-0 mt-2">
                                        {data.benefits.map((benefit, index) => (
                                            <li key={index} className="d-flex mb-3">
                                                <div className="me-3 mt-1">
                                                    <i className="fa-solid fa-circle-check text-success fs-5"></i>
                                                </div>
                                                <div className="text-secondary" style={{ wordBreak: 'keep-all', lineHeight: '1.4', fontSize: '0.95rem' }}>
                                                    {benefit}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 하단 시작하기 버튼 */}
                    <div className="text-center mt-5">
                        {type === 'biz' ? (
                            <button className="btn btn-info btn-lg px-5 py-3 shadow-sm fw-bold text-white" style={{ borderRadius: '4px' }} onClick={() => setLoginModalOpen(true)}>
                                명함 목록 바로가기 <i className="fa-solid fa-arrow-right ms-2"></i>
                            </button>
                        ) : type === 'ai' ? (
                            <button className="btn btn-success btn-lg px-5 py-3 shadow-sm fw-bold text-white" style={{ borderRadius: '4px' }} onClick={() => setLoginModalOpen(true)}>
                                AI 알리미 시작하기 <i className="fa-solid fa-arrow-right ms-2"></i>
                            </button>
                        ) : (
                            <button className="btn btn-primary btn-lg px-5 py-3 shadow-sm fw-bold" style={{ borderRadius: '4px' }} onClick={() => setLoginModalOpen(true)}>
                                중대재해 예방 플랫폼 접속하기 <i className="fa-solid fa-arrow-right ms-2"></i>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <LoginModal
                isOpen={loginModalOpen}
                onClose={() => setLoginModalOpen(false)}
                onLoginSuccess={
                    type === 'biz' ? () => navigate('/biz/card-list') :
                    type === 'ai'  ? () => navigate('/ai') :
                    undefined
                }
            />

            {/* 하단 푸터 */}
            <footer style={{ backgroundColor: "#f5f5f5", fontSize: "1rem", padding: "20px 0" }} className="border-top mt-auto">
                <div className="container">
                    <div className="copyright text-center" style={{ color: "#666" }}>
                        <span>Copyright &copy; 안전 센터 1566-3886</span>
                        <span style={{ marginLeft: "10px" }}>
                            <a href="http://www.atos.co.kr/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>주식회사 아토스</a>
                        </span>
                    </div>
                </div>
            </footer>

        </div>
    );
};

export { GuidePage };